import { createSlice } from '@reduxjs/toolkit';
//
import { dispatch, store } from '../store';
import { allLangs } from 'src/config';
import { setNewUserID } from './app';
import api from 'src/api';
import { isAxiosError } from 'axios';
import { handleApiError } from 'src/utils/handleErrors';

// ----------------------------------------------------------------------
// Initial State type
type InitialStateType = {
  isInitialized: boolean;
  isAuthorization: boolean;
  isError: boolean;
  required:
    | 'email-verification'
    | 'phone-verification'
    | 'profile'
    | 'mfa'
    | 'address'
    | 'reset-password-mfa'
    | 'reset-password'
    | null;
  mustUpdateInfo: boolean;
  user: {
    uid: number;
    name?: string;
    email?: string;
  } | null;
  isChanging: boolean;
  changeError: string;
  changingValue: string;
  preferences: {
    language: string;
    currency: string;
    dateFormat: string;
    timeFormat: string;
    timeZone: string;
    thousandSeparator: string;
    decimalSeparator: string;
  };
  activeTradingSessionsCount: number;
  tariffPromoUsed: boolean;
};
// ----------------------------------------------------------------------
// Get user info response type
type CompleteUserInfoType =
  | {
      required: null;
      uid: number;
      name: string;
      preferences: InitialStateType['preferences'];
      activeTradingSessionsCount: InitialStateType['activeTradingSessionsCount'];
      tariffPromoUsed: InitialStateType['tariffPromoUsed'];
    }
  | {
      required: 'email-verification';
      uid: number;
      email: string;
    }
  | {
      required: 'phone-verification';
      uid: number;
    }
  | {
      required: 'profile';
      uid: number;
    }
  | {
      required: 'address';
      uid: number;
    }
  | {
      required: 'mfa';
      uid: number;
    }
  | {
      required: 'reset-password-mfa';
      uid: number;
      email: string;
    }
  | {
      required: 'reset-password';
      uid: number;
      email: string;
    };

// ----------------------------------------------------------------------
// Auth initial state
const initialState: InitialStateType = {
  // Auth State
  isInitialized: false,
  isAuthorization: false,
  isError: false,
  //
  required: null,
  mustUpdateInfo: false,
  // Auth User Data
  user: null,
  // Preferences State
  isChanging: false,
  changeError: '',
  changingValue: '',
  // Preferences Data
  preferences: {
    language: 'en',
    currency: 'USD',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: 'HH:mm',
    timeZone: 'UTC',
    thousandSeparator: 'space',
    decimalSeparator: 'dot',
  },
  // Current statuses
  activeTradingSessionsCount: 0,
  tariffPromoUsed: false,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // --------------------------------------------------------------
    // Auth User
    authUser(state, action) {
      state.isInitialized = true;
      state.isAuthorization = true;
      state.isError = false;
      state.required = action.payload.required || null;
      state.user = action.payload.user || null;
      state.preferences = {
        ...state.preferences,
        ...(action.payload.preferences || {}),
      };
      state.activeTradingSessionsCount = action.payload.activeTradingSessionsCount || 0;
      state.tariffPromoUsed = action.payload.tariffPromoUsed || false;
    },
    notAuthorized(state) {
      state.isInitialized = true;
      state.isAuthorization = false;
      state.isError = false;
    },
    // Auth Error
    authError(state) {
      state.isInitialized = true;
      state.isAuthorization = false;
      state.isError = true;
    },
    // --------------------------------------------------------------
    // START CHANGING PREFERENCES
    startChanging(state) {
      state.isChanging = true;
      state.changeError = '';
    },
    // CHANGE PREFERENCES ITEM
    changePreferencesItem(state, action) {
      state.isChanging = false;
      state.changeError = '';
      state.changingValue = '';

      if (
        action.payload.decimalSeparator === state.preferences.thousandSeparator ||
        action.payload.thousandSeparator === state.preferences.decimalSeparator
      ) {
        state.preferences = Object.assign({}, state.preferences, {
          [!!action.payload.thousandSeparator ? 'decimalSeparator' : 'thousandSeparator']:
            (action.payload.thousandSeparator || action.payload.decimalSeparator) === 'dot'
              ? 'comma'
              : 'dot',
        });
      }

      state.preferences = Object.assign({}, state.preferences, action.payload);
    },
    // CHANGE PREFERENCES ITEM ERROR
    changePreferencesItemError(state, action) {
      state.isChanging = false;
      state.changeError = action.payload;
    },
    // SET CHANGING VALUE
    setChangingValue(state, action) {
      state.changingValue = action.payload;
    },

    // RESET CHANGE PROPERTIES
    resetChangeProperties(state) {
      state.changingValue = '';
      state.changeError = '';
    },

    // UPDATE SESSIONS AND TARIFFS
    updateSessionsAndTariffs(state, action) {
      if (typeof action.payload.activeTradingSessionsCount === 'number') {
        state.activeTradingSessionsCount = action.payload.activeTradingSessionsCount;
      }
      if (typeof action.payload.tariffPromoUsed === 'boolean') {
        state.tariffPromoUsed = action.payload.tariffPromoUsed;
      }
    },

    // CHANGE NAME
    changeName(state, action) {
      if (state.user) {
        state.user.name = action.payload;
      }
    },

    //
    setDefaultInterfaceLanguage(state, action) {
      if (!localStorage.getItem('lang')) {
        state.preferences.language = action.payload.language;
        localStorage.setItem('lang', action.payload.language);
      }
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------
// Actions
export function authUser() {
  return async () => {
    if (!localStorage.getItem('token')) {
      dispatch(slice.actions.notAuthorized());
      return;
    }

    try {
      const {
        data: { data },
      } = await api.get<{ data: CompleteUserInfoType }>('/account');

      // Check if user id is the same as last
      dispatch(setNewUserID(data.uid));

      // Response
      dispatch(
        slice.actions.authUser({
          user: {
            uid: data.uid,
            ...('email' in data ? { email: data.email } : {}),
            ...('name' in data ? { name: data.name } : {}),
          },
          required: data.required,
          preferences: 'preferences' in data ? data.preferences : null,
          activeTradingSessionsCount:
            'activeTradingSessionsCount' in data ? data.activeTradingSessionsCount : 0,
          tariffPromoUsed: 'tariffPromoUsed' in data ? data.tariffPromoUsed : false,
        })
      );
    } catch (err) {
      if (isAxiosError(err)) {
        if (err?.response?.data?.error?.code === 'UNAUTHORIZED') {
          dispatch(slice.actions.notAuthorized());
        } else {
          dispatch(slice.actions.authError());
        }
      } else {
        dispatch(slice.actions.authError());
      }
    }
  };
}

export function changePreferencesItem(
  key: keyof typeof initialState['preferences'],
  value: string
) {
  return async () => {
    // START CHANGING
    dispatch(slice.actions.startChanging());

    try {
      const { data } = await api.put('/preferences', {
        [key]: value,
      });

      if (!data.success) {
        throw new Error('UNKNOWN_ERROR');
      }

      // Response
      dispatch(slice.actions.changePreferencesItem({ [key]: value }));
    } catch (error) {
      const errorMessage = handleApiError(error);

      dispatch(slice.actions.changePreferencesItemError(errorMessage));
    }
  };
}

export function setChangingPreferencesValue(value: string) {
  return () => {
    dispatch(slice.actions.setChangingValue(value));
  };
}

export function resetPreferencesChangeProperties() {
  return () => {
    dispatch(slice.actions.resetChangeProperties());
  };
}

export function changeName(fullName: string) {
  return () => {
    dispatch(slice.actions.changeName(fullName));
  };
}

export function updateSessionsAndTariffs(info: {
  activeTradingSessionsCount?: number;
  tariffPromoUsed?: boolean;
}) {
  return () => {
    dispatch(slice.actions.updateSessionsAndTariffs(info));
  };
}

export function changeLanguageWithNetwork(lang: string) {
  return async () => {
    // START CHANGING
    dispatch(slice.actions.startChanging());
    // Valid language
    if (allLangs.find((item) => item.value === lang))
      // Response
      dispatch(slice.actions.changePreferencesItem({ language: lang }));
  };
}

export function logoutUser() {
  return async (_: any, getState: ReturnType<typeof store.getState>) => {
    // Logout
    try {
      await api.post('/auth/logout');
    } catch {}
    // Remove token
    localStorage.removeItem('token');

    // Not authorized
    dispatch(slice.actions.notAuthorized());
  };
}

export function cancelSecurityProcess(id?: number | string) {
  return async () => {
    // Cancel reset password mfa
    try {
      await api.delete(`/security-activities/${id || ''}`);
    } catch {}

    // Remove token
    localStorage.removeItem('token');

    // Not authorized
    dispatch(slice.actions.notAuthorized());
  };
}

export function cancelSecurityActivity(id?: number | string) {
  return async () => {
    // Cancel reset password mfa
    try {
      await api.delete(`/security-activities/${id || ''}`);
    } catch {}
  };
}

export function notAuthUser() {
  return () => {
    dispatch(slice.actions.notAuthorized());
  };
}

export function setLocaleLanguage(language?: string) {
  return () => {
    if (language)
      dispatch(
        slice.actions.setDefaultInterfaceLanguage({
          language,
        })
      );
  };
}
